import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, LogOut, LayoutList, UtensilsCrossed, Tag, Clock, BarChart3, Settings as SettingsIcon, Volume2, Maximize, Coffee, Layers } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useKitchenAlerts } from "@/lib/useKitchenAlerts";
import OrdersTab from "@/components/admin/OrdersTab";
import ProductsTab from "@/components/admin/ProductsTab";
import AddonsTab from "@/components/admin/AddonsTab";
import PromotionsTab from "@/components/admin/PromotionsTab";
import HoursTab from "@/components/admin/HoursTab";
import ChiffresTab from "@/components/admin/ChiffresTab";
import ParametresTab from "@/components/admin/ParametresTab";

const TABS = [
  { id: "orders", label: "Commandes", icon: LayoutList },
  { id: "products", label: "Produits", icon: UtensilsCrossed },
  { id: "addons", label: "Addons", icon: Layers },
  { id: "promotions", label: "Promotions", icon: Tag },
  { id: "hours", label: "Horaires", icon: Clock },
  { id: "chiffres", label: "Chiffres", icon: BarChart3 },
  { id: "settings", label: "Paramètres", icon: SettingsIcon },
];

function Login({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { username, password });
      localStorage.setItem("ata_admin_token", data.token);
      toast.success("Connecté");
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur de connexion";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm border-2 border-white p-8 space-y-5" data-testid="admin-login-form">
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Espace admin<span className="text-brand-red">.</span></h1>
        <div><Label>Identifiant</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} className="rounded-none bg-neutral-900 border-white/30" data-testid="admin-username" /></div>
        <div><Label>Mot de passe</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none bg-neutral-900 border-white/30" data-testid="admin-password" /></div>
        {error && <p className="text-sm text-brand-red font-medium" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-none bg-brand-red hover:bg-brand-red/90 h-11" data-testid="admin-login-btn">{loading ? "…" : "Se connecter"}</Button>
        <p className="text-xs text-white/40">Cet appareil restera connecté (trusted device).</p>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("orders");
  const [settings, setSettings] = useState(null);
  const [alertsOn, setAlertsOn] = useState(false);
  const [pending, setPending] = useState(0);
  const [showPrompt, setShowPrompt] = useState(true);
  const { unlock, startAlarm, stopAlarm, isEnabled } = useKitchenAlerts();

  const verify = useCallback(async () => {
    const token = localStorage.getItem("ata_admin_token");
    if (!token) return setAuthed(false);
    try { await api.get("/admin/verify"); setAuthed(true); }
    catch { localStorage.removeItem("ata_admin_token"); setAuthed(false); }
  }, []);

  useEffect(() => { verify(); }, [verify]);
  useEffect(() => { if (authed) api.get("/settings").then((r) => setSettings(r.data)); }, [authed]);

  const enableAlerts = async () => { await unlock(); setAlertsOn(true); setShowPrompt(false); toast.success("Alertes activées — écran maintenu allumé"); };

  const onPending = useCallback((count, prev) => {
    setPending(count);
    if (!isEnabled()) return;
    if (count > 0) startAlarm();
    else stopAlarm();
  }, [isEnabled, startAlarm, stopAlarm]);

  const logout = async () => {
    stopAlarm();
    try { await api.post("/admin/logout"); } catch {}
    localStorage.removeItem("ata_admin_token");
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-black" />;
  if (!authed) return <Login onSuccess={verify} />;

  return (
    <div className="min-h-screen bg-black text-white">
      <Dialog open={authed && !alertsOn && showPrompt}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} hideClose className="w-[calc(100vw-1rem)] max-w-md rounded-none border-2 border-brand-amber bg-neutral-950 text-white p-0" data-testid="alerts-prompt-dialog">
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-brand-amber">
              <Bell className="w-6 h-6" />
              <h2 className="font-display text-2xl font-extrabold tracking-tighter">Activer le mode cuisine</h2>
            </div>
            <p className="text-sm text-white/70">Pour ne manquer aucune commande, activez :</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-3"><Volume2 className="w-5 h-5 text-brand-amber shrink-0" /> La sonnerie sonore répétée à chaque commande</li>
              <li className="flex items-center gap-3"><Maximize className="w-5 h-5 text-brand-amber shrink-0" /> Le plein écran</li>
              <li className="flex items-center gap-3"><Coffee className="w-5 h-5 text-brand-amber shrink-0" /> Le mode anti-veille (écran toujours allumé)</li>
            </ul>
            <Button onClick={enableAlerts} className="w-full rounded-none bg-brand-amber text-black hover:bg-brand-amber/90 h-12 text-base" data-testid="prompt-enable-btn">
              <Bell className="w-4 h-4 mr-2" />Activer maintenant
            </Button>
            <button onClick={() => setShowPrompt(false)} className="w-full text-xs text-white/40 hover:text-white/70" data-testid="prompt-later-btn">Plus tard</button>
          </div>
        </DialogContent>
      </Dialog>

      <header className="sticky top-0 z-40 bg-neutral-950 border-b border-white/15">
        <div className="px-4 h-16 flex items-center justify-between gap-3">
          <span className="font-display text-xl font-extrabold tracking-tighter">ASIA<span className="text-brand-red">.</span>ADMIN {pending > 0 && <span className="ml-2 text-brand-amber">({pending})</span>}</span>
          <div className="flex items-center gap-2">
            {!alertsOn ? (
              <Button onClick={enableAlerts} className="rounded-none bg-brand-amber text-black hover:bg-brand-amber/90 h-9" data-testid="enable-alerts-btn"><Bell className="w-4 h-4 mr-2" />Activer les alertes</Button>
            ) : (
              <span className="text-xs text-brand-amber flex items-center gap-1"><Bell className="w-4 h-4" />Alertes ON</span>
            )}
            <Button onClick={logout} variant="outline" className="rounded-none border-white/30 bg-transparent hover:bg-white/10 h-9" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="px-2 flex gap-1 overflow-x-auto no-scrollbar border-t border-white/10">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-brand-red text-white" : "border-transparent text-white/50 hover:text-white"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="p-4 max-w-7xl mx-auto">
        {tab === "orders" && <OrdersTab settings={settings} onPending={onPending} />}
        {tab === "products" && <ProductsTab />}
        {tab === "addons" && <AddonsTab />}
        {tab === "promotions" && <PromotionsTab />}
        {tab === "hours" && <HoursTab />}
        {tab === "chiffres" && <ChiffresTab />}
        {tab === "settings" && <ParametresTab />}
      </main>
    </div>
  );
}
